<template>
  <div class="hrm_quill_editor mt-2">
    <div>
      <QuillEditor ref="quillEditor" :toolbar="toolbarOptions" :placeholder="placeholder" :theme="editorTheme"
        v-model:content="description" :content-type="contentType" @update:content="update" @ready="onReady">
      </QuillEditor>
    </div>
  </div>
</template>

<script setup>
import { QuillEditor } from "@vueup/vue-quill";
import "@vueup/vue-quill/dist/vue-quill.bubble.css";
import "@vueup/vue-quill/dist/vue-quill.snow.css";
import { ref, watch, defineProps, defineEmits, onMounted } from "vue";

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "What is the current situation/issue?",
  },
  editorTheme: {
    type: String,
    default: "snow",
  },
  contentType: {
    type: String,
    default: "html",
  },
});
const emit = defineEmits(["update:modelValue", "ready"]);

const onReady = () => {
  const quillInstance = quillEditor.value.getQuill();
  emit('ready', quillInstance);
};

const quillEditor = ref(null);

const description = ref([]);

watch(description, (newVal) => {
  emit("update:modelValue", newVal);
});
const update = () => {
  emit("update:modelValue", description.value);
};

const toolbarOptions = [
  [{ header: [1, 2, 3, 4, 5, 6, false] }],
  ["bold", "italic", "underline"],
  ["link"],
  [{ list: "ordered" }, { list: "bullet" }],
  [{ 'align': [] }],
];
onMounted(() => {
  description.value = props.modelValue;
});

const setContent = (content) => {
  if (quillEditor.value) {
    quillEditor.value.setHTML(content);
  }
};

defineExpose({
  setContent,
});
</script>


<style>
.hrm_quill_editor .ql-toolbar {
  background-color: #35373b !important;
  border: none !important;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.hrm_quill_editor .ql-container.ql-snow,
.hrm_quill_editor .ql-container.ql-bubble {
  background-color: #35373b !important;
  color: #ddd !important;
  border: none !important;
  font-size: 14px;
  line-height: 1.5;
  height: 150px;
  overflow-y: auto;
  overflow-x: hidden;
  border-bottom-left-radius: 10px;
}

.hrm_quill_editor .ql-container.ql-snow {
  background-color: #35373b !important;
  color: #ddd !important;
  border: none !important;
  font-size: 14px;
  line-height: 1.5;
  height: 150px;
  overflow-y: auto;
  overflow-x: hidden;
}

.hrm_quill_editor .ql-container .ql-editor {
  padding: 10px 16px !important;
}

.hrm_quill_editor .ql-formats .ql-picker-label:before {
  color: #ddd !important;
}

.hrm_quill_editor .ql-formats .ql-picker-label svg polygon,
.hrm_quill_editor .ql-formats button svg path,
.hrm_quill_editor .ql-formats button.ql-italic svg line,
.hrm_quill_editor .ql-formats button.ql-underline svg rect,
.hrm_quill_editor .ql-formats button.ql-list svg line,
.hrm_quill_editor .ql-formats button.ql-list svg path,
.hrm_quill_editor .ql-formats button.ql-list svg polyline {
  stroke: #ddd !important;
}

.hrm_quill_editor .ql-formats .ql-picker-label:hover:before {
  color: #35373b !important;
}

.hrm_quill_editor .ql-formats .ql-picker-label:hover svg polygon,
.hrm_quill_editor .ql-formats button:hover svg path,
.hrm_quill_editor .ql-formats button.ql-italic:hover svg line,
.hrm_quill_editor .ql-formats button.ql-underline:hover svg rect,
.hrm_quill_editor .ql-formats button.ql-list:hover svg line,
.hrm_quill_editor .ql-formats button.ql-list:hover svg path,
.hrm_quill_editor .ql-formats button.ql-list:hover svg polyline {
  stroke: #35373b !important;
}

.hrm_quill_editor .ql-snow.ql-toolbar .ql-picker-label.ql-active,
.hrm_quill_editor .ql-toolbar.ql-snow .ql-bold.ql-active,
.hrm_quill_editor .ql-toolbar.ql-snow .ql-italic.ql-active,
.hrm_quill_editor .ql-toolbar.ql-snow .ql-underline.ql-active,
.hrm_quill_editor .ql-toolbar.ql-snow .ql-link.ql-active .hrm_quill_editor .ql-toolbar.ql-snow .ql-list.ql-active {
  background-color: #cbcbcb !important;
  border-radius: 5px;
}

.hrm_quill_editor .ql-snow.ql-toolbar .ql-picker-label.ql-active::before,
.hrm_quill_editor .ql-toolbar.ql-snow .ql-bold.ql-active::before,
.hrm_quill_editor .ql-toolbar.ql-snow .ql-italic.ql-active::before,
.hrm_quill_editor .ql-toolbar.ql-snow .ql-underline.ql-active::before,
.hrm_quill_editor .ql-toolbar.ql-snow .ql-link.ql-active::before,
.hrm_quill_editor .ql-toolbar.ql-snow .ql-list.ql-active::before {
  color: #35373b !important;
}

.hrm_quill_editor .ql-snow.ql-toolbar .ql-picker-label.ql-active .ql-stroke,
.hrm_quill_editor .ql-toolbar.ql-snow .ql-bold.ql-active .ql-stroke,
.hrm_quill_editor .ql-toolbar.ql-snow .ql-italic.ql-active .ql-stroke,
.hrm_quill_editor .ql-toolbar.ql-snow .ql-underline.ql-active .ql-stroke,
.hrm_quill_editor .ql-toolbar.ql-snow .ql-link.ql-active .ql-stroke,
.hrm_quill_editor .ql-toolbar.ql-snow .ql-list.ql-active .ql-stroke {
  stroke: #35373b !important;
}

.hrm_quill_editor .ql-toolbar.ql-snow .ql-underline.ql-active .ql-fill {
  fill: #35373b !important;
  stroke: #35373b !important;
}
</style>
